import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Plus,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Copy,
  CheckCircle,
  Building2,
  Fish,
  Users,
  Clock,
} from 'lucide-react';
import { WhaleWallet, KNOWN_WALLETS } from '../../types/whale-tracking';
import { toast } from 'sonner';

export function TrackedWallets() {
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Mock tracked wallets - will be replaced with user's saved wallets
  const [trackedWallets, setTrackedWallets] = useState<WhaleWallet[]>([
    {
      address: '0x28C6c06298d514Db089934071355E5743bf21d60',
      label: 'Binance Hot Wallet',
      category: 'exchange',
      totalValue: '$4.2B',
      lastActivity: Date.now() - 15 * 60 * 1000,
      isTracked: true,
      tags: ['exchange', 'binance', 'hot-wallet'],
    },
    {
      address: '0xDFd5293D8e347dFe59E90eFd55b2956a1343963d',
      label: 'Coinbase',
      category: 'exchange',
      totalValue: '$2.8B',
      lastActivity: Date.now() - 45 * 60 * 1000,
      isTracked: true,
      tags: ['exchange', 'coinbase'],
    },
    {
      address: '0x1234abcd5678efgh9012ijkl3456mnop7890qrst',
      label: 'Whale #1 (Mi seguimiento)',
      category: 'whale',
      totalValue: '$45.2M',
      lastActivity: Date.now() - 2 * 60 * 60 * 1000,
      isTracked: true,
      tags: ['whale', 'custom'],
    },
    {
      address: '0xabcd1234efgh5678ijkl9012mnop3456qrst7890',
      label: 'Smart Money Wallet',
      category: 'institution',
      totalValue: '$125.8M',
      lastActivity: Date.now() - 6 * 60 * 60 * 1000,
      isTracked: true,
      tags: ['institution', 'smart-money'],
    },
  ]);

  const handleAddWallet = () => {
    if (!newWalletAddress || !newWalletAddress.startsWith('0x')) {
      toast.error('Por favor ingresa una direccion de wallet valida');
      return;
    }

    if (trackedWallets.some((w) => w.address.toLowerCase() === newWalletAddress.toLowerCase())) {
      toast.error('Esta wallet ya esta siendo rastreada');
      return;
    }

    // Check if it's a known wallet
    const knownWallet = KNOWN_WALLETS[newWalletAddress];

    const newWallet: WhaleWallet = {
      address: newWalletAddress,
      label: knownWallet?.label || `Wallet ${trackedWallets.length + 1}`,
      category: knownWallet?.category || 'unknown',
      totalValue: 'Cargando...',
      lastActivity: Date.now(),
      isTracked: true,
      tags: ['custom'],
    };

    setTrackedWallets([...trackedWallets, newWallet]);
    setNewWalletAddress('');
    toast.success('Wallet agregada al seguimiento');
  };

  const handleRemoveWallet = (address: string) => {
    setTrackedWallets(trackedWallets.filter((w) => w.address !== address));
    toast.info('Wallet removida del seguimiento');
  };

  const handleToggleTracking = (address: string) => {
    setTrackedWallets(
      trackedWallets.map((w) =>
        w.address === address ? { ...w, isTracked: !w.isTracked } : w
      )
    );
  };

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
    toast.success('Direccion copiada');
  };

  const formatTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));

    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'exchange':
        return <Building2 className="h-4 w-4 text-blue-500" />;
      case 'whale':
        return <Fish className="h-4 w-4 text-purple-500" />;
      case 'institution':
        return <Users className="h-4 w-4 text-green-500" />;
      default:
        return <Fish className="h-4 w-4 text-slate-500" />;
    }
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      exchange: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
      whale: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
      institution: 'bg-green-500/20 text-green-500 border-green-500/30',
      defi_protocol: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
      known_entity: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30',
      unknown: 'bg-slate-500/20 text-slate-500 border-slate-500/30',
    };

    const labels: Record<string, string> = {
      exchange: 'Exchange',
      whale: 'Whale',
      institution: 'Institucion',
      defi_protocol: 'DeFi',
      known_entity: 'Conocido',
      unknown: 'Desconocido',
    };

    return (
      <Badge className={colors[category] || colors.unknown}>
        {labels[category] || 'Desconocido'}
      </Badge>
    );
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 10)}...${address.slice(-8)}`;
  };

  return (
    <div className="space-y-4">
      {/* Add Wallet */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="h-5 w-5 text-violet-500" />
            Agregar Wallet al Seguimiento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="0x..."
              value={newWalletAddress}
              onChange={(e) => setNewWalletAddress(e.target.value)}
              className="font-mono"
            />
            <Button onClick={handleAddWallet}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Recibe alertas cuando esta wallet realice transacciones grandes.
          </p>
        </CardContent>
      </Card>

      {/* Tracked Wallets List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-violet-500" />
              Wallets Rastreadas ({trackedWallets.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trackedWallets.length === 0 ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-400">
              <Fish className="h-12 w-12 mx-auto mb-4 text-slate-400" />
              <p>No tienes wallets en seguimiento</p>
              <p className="text-sm">Agrega una wallet para recibir alertas de sus movimientos</p>
            </div>
          ) : (
            <div className="space-y-3">
              {trackedWallets.map((wallet) => (
                <div
                  key={wallet.address}
                  className={`flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg ${
                    !wallet.isTracked ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white dark:bg-slate-700 rounded-lg">
                      {getCategoryIcon(wallet.category)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900 dark:text-slate-100">
                          {wallet.label}
                        </h3>
                        {getCategoryBadge(wallet.category)}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                          {truncateAddress(wallet.address)}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => copyAddress(wallet.address)}
                        >
                          {copiedAddress === wallet.address ? (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Value */}
                    <div className="text-right hidden md:block">
                      <p className="text-xs text-slate-600 dark:text-slate-400">Valor</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {wallet.totalValue}
                      </p>
                    </div>

                    {/* Last Activity */}
                    <div className="text-right hidden md:block">
                      <p className="text-xs text-slate-600 dark:text-slate-400">Ultima actividad</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-1 justify-end">
                        <Clock className="h-3 w-3" />
                        {formatTimeAgo(wallet.lastActivity)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleTracking(wallet.address)}
                        title={wallet.isTracked ? 'Pausar seguimiento' : 'Activar seguimiento'}
                      >
                        {wallet.isTracked ? (
                          <Eye className="h-4 w-4 text-green-500" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          window.open(`https://etherscan.io/address/${wallet.address}`, '_blank');
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleRemoveWallet(wallet.address)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggested Wallets */}
      <Card className="bg-violet-500/5 border-violet-500/20">
        <CardHeader>
          <CardTitle className="text-lg">Wallets Sugeridas</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Wallets populares que otros usuarios estan rastreando:
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(KNOWN_WALLETS)
              .filter(([addr]) => !trackedWallets.some((w) => w.address === addr))
              .slice(0, 4)
              .map(([address, info]) => (
                <Button
                  key={address}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewWalletAddress(address);
                    handleAddWallet();
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {info.label}
                </Button>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
